import { supabase } from "@/integrations/supabase/client";
import { EventData, ConversationItem } from "../types";
import { getSessionId } from "@/utils/sessionStorage";
import { normalizeDate } from "@/utils/dateUtils";
import { logDebug, logError, logWarn, logInfo } from "@/utils/logger";
import { ensureAnonymousSession } from "@/utils/anonymousSession";

export const fetchCEPInfo = async (cep: string) => {
  try {
    logDebug("Fetching CEP info", { 
      component: 'apiService', 
      action: 'fetchCEPInfo',
      metadata: { hasCep: !!cep }
    });
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    
    if (!data.erro) {
      logDebug("CEP data received successfully", { 
        component: 'apiService', 
        action: 'fetchCEPInfo'
      });
      return {
        neighborhood: data.bairro,
        logradouro: data.logradouro,
        city: data.localidade,
        state: data.uf
      };
    }
    logWarn("Invalid CEP format received", { 
      component: 'apiService', 
      action: 'fetchCEPInfo'
    });
    return null;
  } catch (error) {
    logError("Error fetching CEP info", error as Error, { 
      component: 'apiService', 
      action: 'fetchCEPInfo'
    });
    return null;
  }
};

export const saveEventSimulation = async (eventData: EventData, userId: string | undefined, sessionId?: string, existingSimulationId?: string, source?: string): Promise<string | null> => {
  try {
    logDebug("Starting saveEventSimulation", { 
      component: 'apiService', 
      action: 'saveEventSimulation',
      metadata: {
        hasUserId: !!userId,
        hasSessionId: !!sessionId,
        isUpdate: !!existingSimulationId
      }
    });
    
    const { data: { session } } = await supabase.auth.getSession();
    let currentUserId = session?.user?.id;
    // Se não há sessão alguma, cria uma sessão anônima para que o evento sempre tenha "dono".
    if (!currentUserId) {
      currentUserId = (await ensureAnonymousSession()) ?? undefined;
    }
    const finalUserId = currentUserId || userId;
    
    // Calculate total from selected suppliers
    const calculateTotal = () => {
      if (!eventData.selectedSuppliers) return eventData.calculatedTotal || 0;
      
      let total = 0;
      Object.values(eventData.selectedSuppliers).forEach((categoryItems: any) => {
        if (Array.isArray(categoryItems)) {
          categoryItems.forEach((item: any) => {
            total += item.totalPrice || 0;
          });
        } else if (categoryItems.totalPrice) {
          total += categoryItems.totalPrice;
        }
      });
      return total;
    };

    const contactEmail = eventData.contactEmail || null;
    const contactName = eventData.contactName || null;
    const contactPhone = eventData.contactPhone || null;

    const sid = sessionId || getSessionId() || `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const sessionInfo = {
      sessionId: sid,
      session_id: sid,
      source: source || 'website',
      created_at: new Date().toISOString(),
      browser_fingerprint: typeof navigator !== 'undefined' && navigator.userAgent ? 
        btoa(navigator.userAgent).substring(0, 50) : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    };

    const processedDate = normalizeDate(eventData.date);

    // Generate short event code from first supplier name
    const firstCat = eventData.selectedSuppliers ? Object.values(eventData.selectedSuppliers)[0] : null;
    const firstSup = Array.isArray(firstCat) ? firstCat[0] : firstCat;
    const supName = (firstSup as any)?.name || (firstSup as any)?.supplier?.name || '';
    const cleanName = supName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z]/g, "")
      .toUpperCase();
    const codePrefix = (cleanName.slice(0, 3) || "AME").padEnd(3, "X");
    const codeHex = Math.random().toString(16).slice(2, 6).toUpperCase();

    // Base data for events table
    const eventsData: any = {
      event_date: processedDate,
      cep: eventData.cep || eventData.location?.cep || null,
      guests: eventData.guests ? (typeof eventData.guests === 'string' ? eventData.guests : String(eventData.guests)) : null,
      menu: eventData.selectedSuppliers || {},
      selected_categories: eventData.selectedCategories || null,
      calculated_total: calculateTotal(),
      session_info: sessionInfo,
      status_payments: 'pending',
      status: 'Detalhes da Festa',
      is_active: true,
      origem: 'simulacao',
      event_code: `${codePrefix}-${codeHex}`,
      updated_at: new Date().toISOString(),
      referred_by: typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('ameup_referred_by') || null : null
    };

    if (finalUserId) {
      eventsData.user_id = finalUserId;
    }
    
    // If updating existing event
    if (existingSimulationId) {
      const updateData: any = {
        event_date: processedDate,
        cep: eventData.cep || eventData.location?.cep || null,
        guests: eventData.guests ? (typeof eventData.guests === 'string' ? eventData.guests : String(eventData.guests)) : null,
        menu: eventData.selectedSuppliers || {},
        calculated_total: calculateTotal(),
        updated_at: new Date().toISOString(),
        session_info: sessionInfo
      };

      const { error } = await (supabase as any)
        .from('events')
        .update(updateData)
        .eq('id', existingSimulationId);

      if (error) {
        logError("Error updating simulation", error as Error, { 
          component: 'apiService', 
          action: 'saveEventSimulation'
        });
        throw new Error(`Erro ao atualizar simulação: ${error.message}`);
      }

      return existingSimulationId;
    }

    // Generate UUID client-side to avoid RLS SELECT block after INSERT
    const eventId = crypto.randomUUID();
    eventsData.id = eventId;
    
    // Insert new event (pure INSERT, no SELECT needed)
    const { error: eventError } = await (supabase as any)
      .from('events')
      .insert(eventsData);

    if (eventError) {
      logError("Error saving event simulation", eventError as Error, { 
        component: 'apiService', 
        action: 'saveEventSimulation'
      });
      
      if (eventError.code === '42501') {
        throw new Error("Erro de permissão ao salvar simulação. Tente fazer login novamente.");
      } else if (eventError.code === '23505') {
        throw new Error("Simulação já existe com esses dados.");
      } else {
        throw new Error(`Erro ao salvar os dados do evento: ${eventError.message}`);
      }
    }

    const simulationId = eventsData.id;

    // Insert client info if available
    if (contactEmail || contactName || contactPhone) {
      await (supabase as any).from('event_client_info').insert({
        id: crypto.randomUUID(),
        event_id: simulationId,
        client_name: contactName,
        client_email: contactEmail,
        client_phone: contactPhone
      });
    }

    logInfo("Event simulation saved successfully", { 
      component: 'apiService', 
      action: 'saveEventSimulation'
    });

    return simulationId;
  } catch (error) {
    logError("Error in saveEventSimulation", error as Error, { 
      component: 'apiService', 
      action: 'saveEventSimulation'
    });
    
    if (error instanceof Error && error.message.includes("Erro")) {
      throw error;
    }
    
    throw new Error("Erro inesperado ao salvar simulação");
  }
};

export const validateSimulationAccess = async (simulationId: string, userId?: string, sessionId?: string): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const currentUserId = session?.user?.id;
    const finalUserId = currentUserId || userId;
    
    const { data, error } = await (supabase as any)
      .from('events')
      .select('user_id, session_info')
      .eq('id', simulationId)
      .maybeSingle();

    if (error) {
      logError('Error validating simulation access', error as Error, { 
        component: 'apiService', 
        action: 'validateSimulationAccess'
      });
      return false;
    }

    if (!data) return false;

    if (data.user_id) {
      return data.user_id === finalUserId;
    }

    // For anonymous simulations, allow access
    return true;
  } catch (error) {
    logError('Error in validateSimulationAccess', error as Error, { 
      component: 'apiService', 
      action: 'validateSimulationAccess'
    });
    return false;
  }
};

export const fetchConversationHistory = async (simulationId: string): Promise<ConversationItem[]> => {
  return [];
};

export const saveConversation = async (simulationId: string, userMessage: string, aiResponse: string): Promise<void> => {
  return Promise.resolve();
};
